// Generic Utility Functions for Designer

function parseBoolean(bool) {
  const str = bool ?? "";
  return str.trim().toLowerCase() === "true";
}


function svgToDataURL(svgString) {
  const encodedSvg = encodeURIComponent(svgString);
  return `data:image/svg+xml;charset=utf-8,${encodedSvg}`;
}


async function promiseGetSvgStringFromUrl(url) {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const svgString = await response.text();
    return svgString;
  } catch (error) {
    console.error("Error fetching SVG:", error);
    return null;
  }
}


/**
 * Resizes a canvas element to a specified width and returns a data URL of the resized image.
 *
 * @param {HTMLCanvasElement} canvas The canvas element to resize.
 * @param {number} newWidth The desired width for the resized canvas.
 * @param {string} type The image format for the output (e.g., 'image/png', 'image/jpeg').
 * @param {number} quality The quality of the output image (0-1, for JPEG and WEBP formats).
 * @returns {Promise<string>} A promise that resolves with the data URL of the resized image.
 */
async function resizeCanvas(canvas, newWidth, type = 'image/png', quality = 1.0) {
  return new Promise(resolve => {
    const originalWidth = canvas.width;
    const originalHeight = canvas.height;
    const newHeight = (originalHeight / originalWidth) * newWidth;

    const offscreenCanvas = document.createElement('canvas');
    offscreenCanvas.width = newWidth;
    offscreenCanvas.height = newHeight;
    const ctx = offscreenCanvas.getContext('2d');
    ctx.drawImage(canvas, 0, 0, newWidth, newHeight);

    resolve(offscreenCanvas.toDataURL(type, quality));
  });
}


/**
 * Creates a 200px wide thumbnail from a canvas element.
 *
 * @param {HTMLCanvasElement} canvas The canvas element to create a thumbnail from.
 * @returns {Promise<string>} A promise that resolves with the data URL of the 200px thumbnail.
 */
async function createThumbnail(canvas) {
  return resizeCanvas(canvas, 200);
}


function resizeImageDataURI(dataURI, newWidth, newHeight, format = 'image/jpeg', quality = 1.0) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = function () {
      const canvas = document.createElement('canvas');
      canvas.width = newWidth;
      canvas.height = newHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, newWidth, newHeight);
      const resizedDataURI = canvas.toDataURL(format, quality);
      resolve(resizedDataURI);
    };
    img.src = dataURI;
  });
}


function getMemoryImage(layerJson) {
  //console.log("getMemoryImage", layerJson);

  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      layerJson.image = img;
      resolve(layerJson); // Resolve the promise with the loaded Image object
    };

    img.onerror = (error) => {
      reject(new Error(`Failed to load image from Data URI: ${error}`)); // Reject the promise on error
    };

    img.src = layerJson.image; // Set the source to the Data URI to trigger loading
  });
}


/* Add one or more listeners to an element
 ** @param {DOMElement} element - DOM element to add listeners to
 ** @param {string} eventNames - space separated list of event names, e.g. 'click change'
 ** @param {Function} listener - function to attach for each event as a listener
 */
function addMultipleEventListener(element, eventNames, listener) {
  var events = eventNames.split(" ");
  for (var i = 0, iLen = events.length; i < iLen; i++) {
    element.addEventListener(events[i], listener, false);
  }
}


addMultipleEventListener(document, "mouseup touchend", releaseInterval);
function releaseInterval() {
  if (intervalId) {
    clearInterval(intervalId); // Stop the interval
    intervalId = null; // Reset the interval ID
  }
}


function convertNegativeDegreesToPositive(degrees) {
  // Use modulo 360 to bring it into the -360 to 360 range
  let normalizedDegrees = degrees % 360;

  // If it's still negative, add 360 to make it positive
  if (normalizedDegrees < 0) {
    normalizedDegrees += 360;
  }
  return normalizedDegrees;
}


function removeXmlDeclaration(xmlString) {
  return xmlString.replace(/^<\?xml[^>]*\?>\s*/, "");
}


function arrayBufferToUtf8String(arrayBuffer) {
  const decoder = new TextDecoder("utf-8");
  return decoder.decode(arrayBuffer);
}


function replaceAnyAttributeValue(htmlString, attributeName, newValue) {
  const regex = new RegExp(`${attributeName}\\s?:\\s?#\\s?(.*?)\\s?;`, "gi");

  return htmlString.replace(regex, `${attributeName}:${newValue};`);
}


function getParamsFromUrl(url) {
  url = url.replaceAll("+", "%20");
  url = decodeURIComponent(url);

  if (typeof url === 'string') {
    let params = url.split('?');
    let eachParamsArr = params[1].split('&');
    let obj = {};
    if (eachParamsArr && eachParamsArr.length) {
      eachParamsArr.map(param => {
        let keyValuePair = param.split('=')
        let key = keyValuePair[0];
        let value = keyValuePair[1];
        obj[key] = value;
      })
    }
    return obj;
  }
}


var trimCanvas = (function () {
  function rowBlank(imageData, width, y) {
    for (var x = 0; x < width; ++x) {
      if (imageData.data[y * width * 4 + x * 4 + 3] !== 0) return false;
    }
    return true;
  }

  function columnBlank(imageData, width, x, top, bottom) {
    for (var y = top; y < bottom; ++y) {
      if (imageData.data[y * width * 4 + x * 4 + 3] !== 0) return false;
    }
    return true;
  }

  return function (canvas) {
    var canvas = app.getStage().toCanvas();
    var ctx = canvas.getContext("2d");
    var width = canvas.width;
    var imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    var top = 0, bottom = imageData.height, left = 0, right = imageData.width;

    while (top < bottom && rowBlank(imageData, width, top)) ++top;
    while (bottom - 1 > top && rowBlank(imageData, width, bottom - 1)) --bottom;
    while (left < right && columnBlank(imageData, width, left, top, bottom)) ++left;
    while (right - 1 > left && columnBlank(imageData, width, right - 1, top, bottom)) --right;

    var trimmed = ctx.getImageData(left, top, right - left, bottom - top);
    var copy = canvas.ownerDocument.createElement("canvas");
    var copyCtx = copy.getContext("2d");
    copy.width = trimmed.width;
    copy.height = trimmed.height;
    copyCtx.putImageData(trimmed, 0, 0);

    return copy;
  };
})();


function removeTransparentBorders(dataUrl, filename) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = img.width;
      canvas.height = img.height;
      //console.log("removeTransparentBorders image", canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      let minX = canvas.width, minY = canvas.height;
      let maxX = 0, maxY = 0;

      // Find the bounding box of non-transparent pixels
      for (let y = 0; y < canvas.height; y++) {
        for (let x = 0; x < canvas.width; x++) {
          const alpha = data[((y * canvas.width + x) * 4) + 3];
          if (alpha > 0) { // If not fully transparent
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
          }
        }
      }

      // Calculate new dimensions and draw trimmed image
      const trimmedWidth = maxX - minX + 1;
      const trimmedHeight = maxY - minY + 1;
      //console.log("removeTransparentBorders trimmed", trimmedWidth, trimmedHeight);

      if (trimmedWidth <= 0 || trimmedHeight <= 0) {
        // Image is entirely transparent or invalid
        resolve(dataUrl);
        return;
      }

      const trimmedCanvas = document.createElement('canvas');
      const trimmedCtx = trimmedCanvas.getContext('2d');
      trimmedCanvas.width = trimmedWidth;
      trimmedCanvas.height = trimmedHeight;

      trimmedCtx.drawImage(img, minX, minY, trimmedWidth, trimmedHeight, 0, 0, trimmedWidth, trimmedHeight);

      //console.log("removeTransparentBorders trimmedCanvas", trimmedCanvas.toDataURL());

      const link = document.createElement("a");
      //let filename = "Untitled";
      link.download = filename + ".png";
      link.href = trimmedCanvas.toDataURL();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      baseimagetransformer.show();
      transformer.show();

      resolve(trimmedCanvas.toDataURL());
    };
    img.src = dataUrl;
  });
}


function polarToCartesian(centerX, centerY, radius, angleInDegrees) {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0; // Adjust for SVG's 0-degree being to the right, Konva's Arc often starts from top
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
}


function describeArc(x, y, radius, startAngle, endAngle, sweepFlag = 1) {
  // Ensure the arc goes the full circle if the angle is 360
  if (endAngle - startAngle === 360) {
    endAngle = startAngle + 359.99; // A tiny bit less than 360 to ensure an arc, not just a point
  }

  const start = polarToCartesian(x, y, radius, endAngle); // SVG arc draws from current point to end point
  const end = polarToCartesian(x, y, radius, startAngle);

  const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;
  // Sweep flag: 1 for clockwise, 0 for counter-clockwise
  // Konva's angle is clockwise by default, so a sweepFlag of 1 often makes sense.
  // If you want text on the "inside" or "outside" of the arc, you'll need to adjust the sweepFlag and potentially the start/end angles.

  const d = [
    "M",
    start.x,
    start.y,
    "A",
    radius,
    radius,
    0,
    largeArcFlag,
    sweepFlag,
    end.x,
    end.y,
  ].join(" ");

  // M 400 280 A 120 120 0 0 1 520 400 
  //return "M 0 120 c 60 0 60 -80 120 -80 c 60 0 60 80 120 80 c 60 0 60 -80 120 -80 c 60 0 60 80 120 80"
  //console.log("describeArc", d);

  return d;
}


function getFileExtension(filename) {
  // Split the filename by the dot delimiter
  const parts = removeUrlParameters(filename).split('.');

  // If there's only one part (no dot found) or the filename is just a dot file (e.g., '.gitignore'), return an empty string or handle as needed
  if (parts.length < 2 || parts[0] === '') {
    return '';
  }

  // Get the last element (the extension)
  return parts.pop();
}


function removeFileExtension(filename) {
  return filename.split('.').slice(0, -1).join('.');
}


function removeUrlParameters(url) {
  if (typeof url !== 'string') return '';
  
  // Remove hash first (if any)
  const withoutHash = url.split('#')[0];
  
  // Then remove everything after ?
  const withoutParams = withoutHash.split('?')[0];
  
  return withoutParams;
}

function makeStringHorizontal(str) {
  let cleanedString = str.replace(/(\r\n|\n|\r)/gm, "");
  return cleanedString;
}


function makeStringVertical(str) {
  let verticalString = str;

  if (!hasLinefeed(str)) {
    verticalString = str.split('').join('\n');
  }

  return verticalString;
}


function hasLinefeed(str) {
  return str.includes('\n');
}


